import ErrorComponent from "@/components/atoms/ErrorComponent";
import Loader from "@/components/atoms/Loader";
import {
  AsyncSelectComponent,
  CreatableSelect,
  CustomGoogleMenu,
} from "@/components/atoms/Select";
import { EMAIL_REGEX } from "@/constants/regexs";
import {
  INVALID_EMAIL2_MESSAGE,
  INVALID_FIRSTNAME_MESSAGE,
  INVALID_FORM_MESSAGE,
  INVALID_LASTNAME_MESSAGE,
  INVALID_MAXIMUM_CARACTERE_MESSAGE,
  INVALID_MINIMUM_CARACTERE_MESSAGE,
  INVALID_PHONE2_MESSAGE,
} from "@/constants/validatorMessages";
import getDifferenceBetweenTwoObjects from "@/helpers/FormHelpers";
import useLoadOptions from "@/hooks/useLoadOptions";
import { CardEditProps } from "@/interfaces/Card";
import Instructor, { InstructorForm } from "@/interfaces/Instructor";
import {
  useGetInstructorQuery,
  useUpdateInstructorMutation,
} from "@/services/instructors";
import { FetchBaseQueryError } from "@reduxjs/toolkit/dist/query";
import { isValidPhoneNumber, parsePhoneNumber } from "libphonenumber-js";
import { ElementRef, FC, Fragment, useEffect, useRef } from "react";
import { Button, Card, Col, Form, InputGroup, Row } from "react-bootstrap";
import {
  Controller,
  SubmitErrorHandler,
  SubmitHandler,
  useForm,
} from "react-hook-form";
import { ToastContentProps, toast } from "react-toastify";
import CardHeader from "../../card-headers/CardHeader";
import StoreCourseModal from "../../modals/course/StoreCourseModal";

const InstructorEditCard: FC<CardEditProps<Instructor>> = ({ id }) => {
  const StoreCourseModalRef = useRef<ElementRef<typeof StoreCourseModal>>(null);
  const { data, isLoading, isError, error } = useGetInstructorQuery(id);
  const [updateInstructorAsync] = useUpdateInstructorMutation();

  const {
    control,
    handleSubmit,
    register,
    reset,
    formState: { errors },
  } = useForm<Partial<InstructorForm>>();

  useEffect(() => {
    data &&
      reset({
        ...data,
        areas: data.areas.map((areas) => ({
          label: areas,
          value: areas,
        })),
      });
  }, [data]);

  const onSubmit: SubmitHandler<Partial<InstructorForm>> = async ({
    areas,
    ...newData
  }) => {
    const result = getDifferenceBetweenTwoObjects<Partial<InstructorForm>>(
      data,
      newData
    );
    const phone = result.phone && parsePhoneNumber(result.phone, "FR").number;
    toast.promise(
      updateInstructorAsync([
        id,
        {
          ...result,
          phone,
          areas: areas && areas.map((element) => element.value),
        },
      ]).unwrap(),
      {
        pending: "Modification du formateur en cours ...",
        success: {
          render() {
            return (
              <p style={{ marginBottom: 0, textAlign: "center" }}>
                Le formateur à bien été modifié 🤩
              </p>
            );
          },
        },
        error: {
          render({ data }: ToastContentProps<FetchBaseQueryError>) {
            if (data?.status === 422) {
              return "Les champs que vous avez remplis semblent être incorrects.";
            }

            return "Une erreur est survenue";
          },
        },
      }
    );
  };

  const onError: SubmitErrorHandler<InstructorForm> = () =>
    toast.error(INVALID_FORM_MESSAGE);

  if (isLoading) {
    return <Loader absolute />;
  } else if (isError) {
    return <ErrorComponent error={error} absolute returnButton />;
  }

  return (
    <Fragment>
      <Controller
        control={control}
        name="courses"
        render={({ field }) => (
          <StoreCourseModal
            ref={StoreCourseModalRef}
            callback={(course) =>
              field.onChange([...(field.value ? field.value : []), course])
            }
          />
        )}
      />
      <Form onSubmit={handleSubmit(onSubmit, onError)}>
        <Card>
          <CardHeader title="Modification du formateur" />
          <Card.Body className="bg-light">
            <Row className="gx-3">
              <Col lg={6}>
                <Form.Group className="mb-3">
                  <Form.Label>
                    Prenom <span className="text-danger">*</span> :
                  </Form.Label>
                  <Form.Control
                    placeholder="ex : Remi"
                    type="text"
                    {...register("firstname", {
                      required: INVALID_FIRSTNAME_MESSAGE,
                      minLength: {
                        value: 2,
                        message: INVALID_MINIMUM_CARACTERE_MESSAGE,
                      },
                      maxLength: {
                        value: 255,
                        message: INVALID_MAXIMUM_CARACTERE_MESSAGE,
                      },
                    })}
                    isInvalid={!!errors.firstname}
                  />
                  <Form.Control.Feedback type="invalid">
                    {errors.firstname?.message}
                  </Form.Control.Feedback>
                </Form.Group>
              </Col>
              <Col lg={6}>
                <Form.Group className="mb-3">
                  <Form.Label>
                    Nom de famille <span className="text-danger">*</span> :
                  </Form.Label>
                  <Form.Control
                    placeholder="ex : Dupont"
                    type="text"
                    {...register("lastname", {
                      required: INVALID_LASTNAME_MESSAGE,
                      minLength: {
                        value: 2,
                        message: INVALID_MINIMUM_CARACTERE_MESSAGE,
                      },
                      maxLength: {
                        value: 255,
                        message: INVALID_MAXIMUM_CARACTERE_MESSAGE,
                      },
                    })}
                    isInvalid={!!errors.lastname}
                  />
                  <Form.Control.Feedback type="invalid">
                    {errors.lastname?.message}
                  </Form.Control.Feedback>
                </Form.Group>
              </Col>
              <Col lg={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Email :</Form.Label>
                  <Form.Control
                    placeholder="ex : remi@groupe-pi.com"
                    type="email"
                    {...register("email", {
                      pattern: {
                        value: EMAIL_REGEX,
                        message: INVALID_EMAIL2_MESSAGE,
                      },
                    })}
                    isInvalid={!!errors.email}
                  />
                  <Form.Control.Feedback type="invalid">
                    {errors.email?.message}
                  </Form.Control.Feedback>
                </Form.Group>
              </Col>
              <Col lg={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Numéro de téléphone :</Form.Label>
                  <Form.Control
                    placeholder="ex : +33 6 34 83 55 32"
                    type="text"
                    {...register("phone", {
                      validate: (value) => {
                        if (value && !isValidPhoneNumber(value, "FR")) {
                          return INVALID_PHONE2_MESSAGE;
                        }
                      },
                    })}
                    isInvalid={!!errors.phone}
                  />
                  <Form.Control.Feedback type="invalid">
                    {errors.phone?.message}
                  </Form.Control.Feedback>
                </Form.Group>
              </Col>
              <Col lg={6}>
                <Form.Group className="mb-2">
                  <Form.Label>Siret :</Form.Label>
                  <Form.Control
                    placeholder="ex : 552 178 639 00132"
                    type="text"
                    {...register("siret", {
                      validate: {
                        isLength15: (value) => {
                          if (value && value.length !== 14) {
                            return "Le numéro de siret doit comporter 14 chiffres";
                          }
                        },
                      },
                    })}
                    isInvalid={!!errors.siret}
                  />
                  <Form.Control.Feedback type="invalid">
                    {errors.siret?.message}
                  </Form.Control.Feedback>
                </Form.Group>
                <Form.Group className="mb-3">
                  <Form.Check
                    {...register("withTva")}
                    label="Le formateur est-il assujeti à la TVA ?"
                  />
                  <Form.Control.Feedback type="invalid">
                    {errors.withTva?.message}
                  </Form.Control.Feedback>
                </Form.Group>
              </Col>
              <Col lg={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Adresse</Form.Label>
                  <Form.Control
                    placeholder="ex : 3 Rue de la Paix, 75002 Paris"
                    type="string"
                    {...register("address")}
                    isInvalid={!!errors.address}
                  />
                  <Form.Control.Feedback type="invalid">
                    {errors.address?.message}
                  </Form.Control.Feedback>
                </Form.Group>
              </Col>
              <Col lg={3}>
                <Form.Group className="mb-3">
                  <Form.Label>Ville :</Form.Label>
                  <Form.Control
                    placeholder="ex : 3 Rue de la Paix, 75002 Paris"
                    type="string"
                    {...register("city")}
                    isInvalid={!!errors.city}
                  />
                  <Form.Control.Feedback type="invalid">
                    {errors.city?.message}
                  </Form.Control.Feedback>
                </Form.Group>
              </Col>
              <Col lg={3}>
                <Form.Group className="mb-3">
                  <Form.Label>Code postale: </Form.Label>
                  <Form.Control
                    placeholder="ex : 3 Rue de la Paix, 75002 Paris"
                    type="string"
                    {...register("zipCode")}
                    isInvalid={!!errors.zipCode}
                  />
                  <Form.Control.Feedback type="invalid">
                    {errors.zipCode?.message}
                  </Form.Control.Feedback>
                </Form.Group>
              </Col>
            </Row>
            <Row className="gx-3">
              <Col lg={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Prix hors taxe :</Form.Label>
                  <InputGroup>
                    <Form.Control
                      placeholder="ex : 200"
                      type="number"
                      {...register("priceHt")}
                      isInvalid={!!errors.priceHt}
                    />
                    <InputGroup.Text>€</InputGroup.Text>
                  </InputGroup>
                  <Form.Control.Feedback type="invalid">
                    {errors.priceHt?.message}
                  </Form.Control.Feedback>
                </Form.Group>
              </Col>

              <Col lg={6}>
                <Form.Group className="mb-3">
                  <Form.Label>NDA :</Form.Label>
                  <Form.Control
                    placeholder="ex : je sais pas encore"
                    type="text"
                    {...register("nda")}
                    isInvalid={!!errors.nda}
                  />
                  <Form.Control.Feedback type="invalid">
                    {errors.nda?.message}
                  </Form.Control.Feedback>
                </Form.Group>
              </Col>
              <Col lg={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Région(s) :</Form.Label>
                  <Controller
                    control={control}
                    name="areas"
                    render={({ field }) => (
                      <CreatableSelect
                        isMulti
                        onChange={field.onChange}
                        value={field.value}
                      />
                    )}
                  />
                  <Form.Control.Feedback type="invalid">
                    {errors.areas?.message}
                  </Form.Control.Feedback>
                </Form.Group>
              </Col>
            </Row>
            <Button type="submit" variant="success" className="mt-3">
              Confirmer
            </Button>
          </Card.Body>
        </Card>
      </Form>
    </Fragment>
  );
};

export default InstructorEditCard;
